import type { GuidePage } from '../guides';

const example = [
  { item: 'Labor: 2 techs x 3.5 hours on site (house wash and driveway), at your loaded hourly cost', amount: '$315' },
  { item: 'Chemicals: sodium hypochlorite mix, surfactant, post-treat for the concrete', amount: '$38' },
  { item: 'Drive time and setup: 40 minutes round trip, rig and fuel', amount: '$62' },
  { item: 'Overhead share: insurance, equipment wear, office time for this job', amount: '$85' },
  { item: 'Profit margin you choose for the job', amount: '$130' },
];

const faq = [
  {
    q: 'Should I charge pressure washing by the hour or by the square foot?',
    a: 'Customers prefer a fixed price per job, and most companies quote by area: square feet of siding and flatwork. Behind the scenes, though, the price should come from the hours the job will take. Square-foot prices are a shortcut that works once you know your own production rates.',
  },
  {
    q: 'How do I work out my production rate?',
    a: 'Time a few jobs from setup to pack-up and divide the area cleaned by the hours. Do it separately for siding, flat concrete and wood, because each goes at a different speed. Update the numbers each season as your crew and equipment change.',
  },
  {
    q: 'Do I need a minimum charge?',
    a: 'Most pressure washing companies set one, because every job carries drive time, setup and cleanup no matter how small the surface is. Work out what a short job really costs you and make that your floor.',
  },
  {
    q: 'How should I price roofs?',
    a: 'Separately from siding, using roof-specific production rates: soft washing a roof is slower, needs more chemical and carries more risk. Many companies also add a height or pitch factor.',
  },
];

const page: GuidePage = {
  slug: 'how-to-price-pressure-washing',
  path: '/resources/how-to-price-pressure-washing',
  primaryKeyword: 'how to price pressure washing',
  secondaryKeywords: ['pressure washing pricing guide', 'how much to charge for pressure washing', 'house washing price per square foot'],
  title: 'How to Price Pressure Washing: A Working Method | Scopie',
  metaDescription:
    'Price pressure washing from your real costs: production rates per surface, chemicals, drive time, overhead and margin, turned into a per-job quote.', // 147
  h1: 'How to price pressure washing jobs',
  answerFirst:
    'Price pressure washing from what each job costs you: labor hours from your production rate for each surface, chemicals, drive time, a share of overhead, then the margin you want. Turn that into a per-job or per-square-foot price for the customer. The method below works for house washes, flatwork and roofs.',
  datePublished: '2026-10-07',
  lastReviewed: '2026-10-07',
  blocks: [
    {
      type: 'list',
      id: 'method',
      h2: 'The five parts of a pressure washing price',
      style: 'steps',
      items: [
        { title: 'Labor hours', text: 'Area of each surface divided by your production rate for that surface, plus setup and pack-up time, times the number of techs.' },
        { title: 'Loaded labor cost', text: 'What an hour of each tech costs you, including payroll taxes and workers compensation, not just the wage.' },
        { title: 'Chemicals and consumables', text: 'Mix, surfactant, post-treatment, and wear parts like nozzles and surface cleaner tips.' },
        { title: 'Travel and overhead', text: 'Drive time and fuel to this job, plus a share of insurance, equipment depreciation, marketing and admin.' },
        { title: 'Margin', text: 'The profit you add on top. Decide it on purpose rather than leaving whatever is left over.' },
      ],
    },
    {
      type: 'sample-quote',
      id: 'example',
      h2: 'A worked example: house wash and driveway',
      label: 'How one job adds up (internal costing, not the customer quote)',
      lines: example,
      total: '$630',
      totalLabel: 'Price to quote for this job',
      note: 'Illustrative numbers. Use your own costs and production rates; local markets vary widely.',
    },
    {
      type: 'text',
      id: 'per-square-foot',
      h2: 'From cost to a per-square-foot price',
      lead: 'Customers ask "what do you charge per square foot?" Your costs do not come per square foot.',
      paragraphs: [
        'Once you have costed a handful of real jobs this way, divide each job price by the area cleaned. The results give you working per-square-foot prices for siding, flat concrete and wood that already include your overhead and margin. Use those for fast quotes, and fall back to the full method for unusual jobs: three storeys, heavy oxidation, limited water or long carries.',
        'Keep the surfaces separate on the quote you send. A customer who sees siding, driveway and deck priced on their own lines understands the total and can drop a surface instead of asking for a discount.',
      ],
    },
    {
      type: 'list',
      id: 'adjust',
      h2: 'What should move the price up or down',
      style: 'bullets',
      items: [
        { title: 'Height and access', text: 'Second and third storeys, steep roofs and tight side yards slow the crew.' },
        { title: 'Condition', text: 'Heavy mildew, oxidized siding or years of oil on a driveway take longer and more chemical.' },
        { title: 'Water supply', text: 'A weak tap or none at all may mean hauling water.' },
        { title: 'Season', text: 'Spring demand fills calendars; some companies price off-season work to keep crews busy.' },
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Pricing mistakes that keep pressure washers busy and broke',
      style: 'mistakes',
      items: [
        { title: 'Copying a competitor\'s flyer', text: 'Their costs, crew speed and insurance are not yours.' },
        { title: 'Pricing only the time on the wand', text: 'Setup, drive and pack-up are paid hours too.' },
        { title: 'Forgetting equipment wear', text: 'Pumps, hoses and surface cleaners wear out; their cost belongs in overhead.' },
        { title: 'Never revisiting prices', text: 'Chemical and fuel costs move every year. Re-cost a few jobs each season.' },
      ],
    },
    {
      type: 'text',
      id: 'quote',
      h2: 'Putting the price on a quote',
      paragraphs: [
        'In Scopie you save each surface as an item with your working price, add the ones a job needs, and mark extras like gutter brightening as optional. The quote is emailed to the customer as a link; they tick the extras they want and sign. Your plant, paint and weather clauses come from your saved terms.',
      ],
      links: [{ phrase: 'save each surface as an item with your working price', href: '/for/pressure-washing' }],
    },
    { type: 'faq', id: 'faq', h2: 'Pressure washing pricing questions', items: faq },
  ],
  faq,
  internalLinks: [
    { href: '/for/pressure-washing', anchor: 'Scopie for pressure washing businesses', status: 'live' },
    { href: '/templates/pressure-washing-quote-template', anchor: 'pressure washing quote template', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
  ],
  cta: {
    heading: 'Turn your prices into quotes customers sign',
    text: 'Save each surface at your working price in Scopie and build the next house-wash quote in a couple of minutes.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
};

export default page;
